import { CheckBoxOutlineBlankOutlined, CheckBoxOutlined, Close } from '@mui/icons-material';
import { Box, Checkbox, CircularProgress, IconButton, InputAdornment, MenuItem, Popover, TextField, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

export type ListProps = {
    open: boolean;
    anchorEl: HTMLElement | null;
    searchInputRef: React.RefObject<HTMLInputElement | null>;
    containerRef: React.RefObject<HTMLDivElement | null>;
    searchText: string;
    options: any[];
    filteredOptions: any[];
    selectedOptions: number[];
    allSelected: boolean;
    onClose: () => void;
    onChangeSearch: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onSelectOption: (selectedId: number[]) => () => void;
    onClearSearch: () => void;
    onSelectAll: () => void;
    onUnselectAll: () => void;
    disabled?: boolean;
    loading?: boolean;
};

export const List = ({
    open,
    anchorEl,
    searchInputRef,
    containerRef,
    searchText,
    options,
    filteredOptions,
    selectedOptions,
    allSelected,
    onClose,
    onChangeSearch,
    onSelectOption,
    onClearSearch,
    onSelectAll,
    onUnselectAll,
    disabled,
    loading
}: ListProps) => {
    const computedFilteredOptions = useMemo(() => {
        if (disabled) {
            return null;
        }

        if (loading) {
            return <CircularProgress size="1rem" />;
        }

        if (filteredOptions.length) {
            return filteredOptions
                .filter((el) => !selectedOptions.includes(el.id))
                .map((el) => (
                    <Box key={el.id}>
                        <MenuItem
                            sx={{ bgcolor: '#F5F6F7', px: '0.5rem', height: '2rem' }}
                            onClick={(event) => {
                                event.stopPropagation();
                                onSelectOption([...selectedOptions, el.id])();
                            }}
                        >
                            <>
                                <Checkbox
                                    sx={(theme) => ({
                                        p: 0,
                                        pr: '0.5rem',
                                        color: theme.palette.primary[400],
                                        '&.Mui-checked': { color: theme.palette.primary.dark }
                                    })}
                                    checked={false}
                                    size="small"
                                    icon={<CheckBoxOutlineBlankOutlined />}
                                />
                                <Typography
                                    title={el.label}
                                    fontSize="0.875rem"
                                    sx={{ overflow: 'hidden', width: '100%', textOverflow: 'ellipsis' }}
                                >
                                    {el.label}
                                </Typography>
                            </>
                        </MenuItem>
                    </Box>
                ));
        }

        return (
            <Typography fontSize="14px" sx={{ py: 2, px: 1 }} color="#C6C4C4" align="center">
                <FormattedMessage id="noOptionsAvailable" />
            </Typography>
        );
    }, [disabled, filteredOptions, onSelectOption, selectedOptions, loading]);

    return (
        <Popover
            anchorEl={anchorEl}
            open={open}
            onClose={onClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            PaperProps={{
                sx: {
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0,
                    borderBottomLeftRadius: '0.5rem',
                    borderBottomRightRadius: '0.5rem',
                    width: `calc(${containerRef.current?.clientWidth}px + 1.5rem)`
                }
            }}
        >
            <Box sx={{ maxHeight: '30rem', overflow: 'hidden' }}>
                {selectedOptions.length > 0 && (
                    <Box>
                        <Box
                            sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column-reverse',
                                overflowY: 'auto',
                                border: '1px solid #ccc',
                                maxHeight: '15rem'
                            }}
                        >
                            {options
                                .filter((el) => selectedOptions.includes(el.id))
                                .map((el) => (
                                    <Box key={el.id} sx={{ backgroundColor: disabled ? '#E0E0E0' : '#FFFFFF' }}>
                                        <MenuItem
                                            sx={{ backgroundColor: disabled ? '#E0E0E0' : '#FFFFFF', px: '0.5rem', height: '2rem' }}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                if (disabled) return;
                                                onSelectOption(selectedOptions.filter((opt) => opt !== el.id))();
                                            }}
                                        >
                                            <>
                                                {disabled ? null : (
                                                    <Checkbox
                                                        sx={(theme) => ({
                                                            p: 0,
                                                            pr: '0.5rem',
                                                            color: theme.palette.primary[400],
                                                            '&.Mui-checked': { color: theme.palette.primary.dark }
                                                        })}
                                                        checked
                                                        size="small"
                                                        checkedIcon={<CheckBoxOutlined />}
                                                    />
                                                )}
                                                <Typography
                                                    title={el.label}
                                                    fontSize="0.875rem"
                                                    sx={{ overflow: 'hidden', width: '100%', textOverflow: 'ellipsis' }}
                                                >
                                                    {el.label}
                                                </Typography>
                                            </>
                                        </MenuItem>
                                    </Box>
                                ))}
                        </Box>

                        <MenuItem
                            key="unselectAll"
                            sx={{
                                bgcolor: '#FFF',
                                height: '2rem',
                                px: '0.5rem'
                            }}
                            onClick={onUnselectAll}
                        >
                            <>
                                <Typography fontSize="0.875rem" sx={{ overflow: 'hidden', width: '100%', textOverflow: 'ellipsis' }}>
                                    Unselect All
                                </Typography>
                            </>
                        </MenuItem>
                    </Box>
                )}
                <TextField
                    variant="filled"
                    onClick={(event) => {
                        event.stopPropagation();
                    }}
                    size="small"
                    inputRef={searchInputRef}
                    InputProps={{
                        hiddenLabel: true,
                        disableUnderline: true,
                        sx: (theme) => ({
                            borderTop: `solid 1px ${theme.palette.primary[500]}`,
                            borderBottom: `solid 1px ${theme.palette.primary[500]}`,
                            backgroundColor: theme.palette.secondary.light,
                            '& .MuiInputBase-input': {
                                color: '#000',
                                '&:focus': { color: '#000', backgroundColor: theme.palette.secondary.light }
                            },
                            '& .MuiInputAdornment-root': {
                                '&:hover': { backgroundColor: theme.palette.secondary.light }
                            }
                        }),
                        endAdornment: searchText.length ? (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="clear search"
                                    onClick={onClearSearch}
                                    edge="end"
                                    sx={{ backgroundColor: 'transparent' }}
                                >
                                    <Close sx={{ color: '#000', fontSize: '1rem' }} />
                                </IconButton>
                            </InputAdornment>
                        ) : undefined
                    }}
                    value={searchText}
                    onChange={onChangeSearch}
                    autoFocus
                    fullWidth
                />

                <Box>
                    {!allSelected && searchText.length === 0 && (
                        <MenuItem
                            key="selectAll"
                            sx={(theme) => ({
                                borderBottom: `solid 1px ${theme.palette.primary[500]}`,
                                bgcolor: '#FFF',
                                height: '2rem',
                                px: '0.5rem'
                            })}
                            onClick={onSelectAll}
                        >
                            <Checkbox
                                sx={(theme) => ({
                                    p: 0,
                                    pr: '0.5rem',
                                    color: theme.palette.primary[400],
                                    '&.Mui-checked': { color: theme.palette.primary.dark }
                                })}
                                checked={allSelected}
                                size="small"
                                icon={<CheckBoxOutlineBlankOutlined />}
                                checkedIcon={<CheckBoxOutlined />}
                            />
                            <Typography fontSize="0.875rem" sx={{ overflow: 'hidden', width: '100%', textOverflow: 'ellipsis' }}>
                                Select All
                            </Typography>
                        </MenuItem>
                    )}
                    <Box
                        sx={{
                            flex: 1,
                            overflowY: 'auto',
                            maxHeight: '15rem'
                        }}
                    >
                        {computedFilteredOptions}
                    </Box>
                </Box>
            </Box>
        </Popover>
    );
};
